import { useMemo } from "react";
import { FaRegEdit } from "react-icons/fa";
import {
  HiOutlineExclamationTriangle,
  HiOutlineGift,
  HiOutlineFunnel,
  HiOutlinePlus,
  HiOutlineTrash,
} from "react-icons/hi2";
import { IGift } from "@/api_framework/api_modals/GiftConfig";
import RBACFlag from "@/app/components/core/RBACFlag";
import { useUtils } from "@/app/hooks/useUtils";
import TableComponent, { type TableColumn } from "@/app/components/ui/Reusable/TableComponent";
import StructuredTableSkeleton from "@/app/components/ui/Skeletons/StructuredTableSkeleton";
import { ConfirmModal, StatusBadge } from "@/app/components/routes/auth_routes/QuickCommerce/HomeTileConfig/components";
import { SegmentedControl } from "@/app/components/routes/auth_routes/QuickCommerce/HomeTileConfig/controls";
import {
  primaryButtonClassName,
  secondaryButtonClassName,
} from "@/app/components/routes/auth_routes/QuickCommerce/HomeTileConfig/interface";
import { GiftThumbnail } from "../components";
import { GIFT_STATUS_FILTERS, GIFT_TYPE_LABEL, GiftStatusFilter, toBadgeStatus } from "../interface";

export const GiftToolbar = ({
  statusFilter,
  onStatusFilterChange,
  onAddGift,
}: {
  statusFilter: GiftStatusFilter;
  onStatusFilterChange: (next: GiftStatusFilter) => void;
  onAddGift: () => void;
}) => (
  <div className="flex flex-wrap items-center justify-between gap-x-6 gap-y-3 border-b border-slate-100 bg-slate-50/60 px-5 py-3">
    <SegmentedControl<GiftStatusFilter>
      label="Status"
      options={GIFT_STATUS_FILTERS}
      value={statusFilter}
      onChange={onStatusFilterChange}
    />
    <RBACFlag actionId="GIFT_SETUP_CREATION">
      <button type="button" onClick={onAddGift} className={primaryButtonClassName}>
        <HiOutlinePlus className="h-4 w-4" />
        Create New Gift
      </button>
    </RBACFlag>
  </div>
);

export const useGiftColumns = ({
  onEdit,
  onDelete,
}: {
  onEdit: (giftId: string) => void;
  onDelete: (gift: IGift) => void;
}): TableColumn<IGift>[] => {
  const { veticDateFormat } = useUtils();

  return useMemo<TableColumn<IGift>[]>(
    () => [
      {
        id: "gift_name",
        header: "Gift",
        minWidth: 280,
        cell: ({ row }) => (
          <div className="flex items-center gap-3">
            <GiftThumbnail imageUrl={row.gift_image} alt={row.gift_name} className="h-12 w-12" />
            <div className="min-w-0">
              <p className="truncate text-xs font-semibold text-gray-900">{row.gift_name}</p>
              <p className="truncate text-[11px] text-slate-500">{row.heading || row.gift_tag}</p>
            </div>
          </div>
        ),
        headerClassName: "text-slate-500",
      },
      {
        id: "gift_type",
        header: "Gift Type",
        width: 160,
        cell: ({ row }) => (
          <span className="text-xs text-slate-700">{GIFT_TYPE_LABEL[row.gift_type] ?? row.gift_type}</span>
        ),
        headerClassName: "text-slate-500",
      },
      {
        id: "created_by",
        header: "Created By",
        minWidth: 200,
        cell: ({ row }) => (
          <span className="block truncate text-xs text-slate-700" title={row.created_by_email ?? undefined}>
            {row.created_by_email || "—"}
          </span>
        ),
        headerClassName: "text-slate-500",
      },
      {
        id: "created_at",
        header: "Created At",
        width: 130,
        cell: ({ row }) => <span className="text-xs text-slate-700">{veticDateFormat(row.created_at) || "—"}</span>,
        headerClassName: "text-slate-500",
      },
      {
        id: "status",
        header: "Status",
        width: 120,
        cell: ({ row }) => <StatusBadge status={toBadgeStatus(row.status)} />,
        headerClassName: "text-slate-500",
      },
      {
        id: "action",
        header: "Action",
        align: "center",
        width: 120,
        cell: ({ row }) => (
          <div className="flex items-center justify-center gap-1">
            <RBACFlag actionId="GIFT_SETUP_CREATION">
              <button
                type="button"
                onClick={() => row.gift_id && onEdit(row.gift_id)}
                className="rounded-lg border border-transparent p-2 text-[#193A6A] transition-colors hover:border-slate-200 hover:bg-slate-50"
                aria-label={`Edit gift ${row.gift_name}`}
                title="Edit gift"
              >
                <FaRegEdit className="h-4 w-4" />
              </button>
            </RBACFlag>
            <RBACFlag actionId="GIFT_SETUP_CREATION">
              <button
                type="button"
                onClick={() => onDelete(row)}
                className="rounded-lg border border-transparent p-2 text-rose-600 transition-colors hover:border-rose-100 hover:bg-rose-50"
                aria-label={`Delete gift ${row.gift_name}`}
                title="Delete gift"
              >
                <HiOutlineTrash className="h-4 w-4" />
              </button>
            </RBACFlag>
          </div>
        ),
        headerClassName: "text-slate-500 text-center",
      },
    ],
    [onEdit, onDelete],
  );
};

const EmptyGiftState = ({
  hasFilters,
  onClearFilters,
  onAddGift,
}: {
  hasFilters: boolean;
  onClearFilters: () => void;
  onAddGift: () => void;
}) => (
  <div className="flex flex-col items-center px-5 py-14 text-center">
    <span className="inline-flex h-12 w-12 items-center justify-center rounded-full bg-[#EEF5FF] text-[#193A6A]">
      {hasFilters ? <HiOutlineFunnel className="h-6 w-6" /> : <HiOutlineGift className="h-6 w-6" />}
    </span>
    <h3 className="mt-3 text-sm font-semibold text-gray-900">{hasFilters ? "No gift found" : "No gift yet"}</h3>
    <p className="mt-1 max-w-sm text-xs leading-5 text-gray-500">
      {hasFilters
        ? "No gift matches the selected status. Try clearing the filter."
        : "Create a gift to reward eligible carts with a free or discounted item."}
    </p>
    {hasFilters ? (
      <button type="button" onClick={onClearFilters} className={`mt-4 ${secondaryButtonClassName}`}>
        Clear filter
      </button>
    ) : (
      <RBACFlag actionId="GIFT_SETUP_CREATION">
        <button type="button" onClick={onAddGift} className={`mt-4 ${primaryButtonClassName}`}>
          <HiOutlinePlus className="h-4 w-4" />
          Create New Gift
        </button>
      </RBACFlag>
    )}
  </div>
);

export const GiftTable = ({
  columns,
  gifts,
  isLoading,
  error,
  hasFilters,
  onRetry,
  onClearFilters,
  onAddGift,
}: {
  columns: TableColumn<IGift>[];
  gifts: IGift[];
  isLoading: boolean;
  error: string | null;
  hasFilters: boolean;
  onRetry: () => void;
  onClearFilters: () => void;
  onAddGift: () => void;
}) => {
  if (isLoading) {
    return (
      <div className="px-5 py-4">
        <StructuredTableSkeleton columns={columns.length} />
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col items-center px-5 py-14 text-center">
        <span className="inline-flex h-12 w-12 items-center justify-center rounded-full bg-amber-50 text-amber-600">
          <HiOutlineExclamationTriangle className="h-6 w-6" />
        </span>
        <h3 className="mt-3 text-sm font-semibold text-gray-900">{error}</h3>
        <button type="button" onClick={onRetry} className={`mt-4 ${secondaryButtonClassName}`}>
          Retry
        </button>
      </div>
    );
  }

  if (!gifts.length) {
    return <EmptyGiftState hasFilters={hasFilters} onClearFilters={onClearFilters} onAddGift={onAddGift} />;
  }

  return (
    <TableComponent<IGift>
      columns={columns}
      data={gifts}
      getRowId={(row) => row.gift_id ?? row.gift_name}
      containerClassName="!mr-0 !rounded-none !border-0 bg-white"
      scrollContainerClassName="overflow-x-auto max-h-[calc(100vh-320px)]"
      minTableWidthClassName="min-w-full"
      tableClassName="min-w-full divide-y divide-slate-100"
      stickyHeader
      wrapCells
    />
  );
};

export const DeleteGiftModal = ({
  target,
  isLoading,
  onConfirm,
  onClose,
}: {
  target: IGift | null;
  isLoading: boolean;
  onConfirm: () => void;
  onClose: () => void;
}) => (
  <ConfirmModal
    isOpen={Boolean(target)}
    title="Delete this gift?"
    description={
      <>
        The gift{target?.gift_name ? ` “${target.gift_name}”` : ""} will no longer be offered on the cart. This cannot
        be undone.
      </>
    }
    isLoading={isLoading}
    onConfirm={onConfirm}
    onClose={onClose}
  />
);
